<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="text-center pa-6">
      <v-progress-circular indeterminate color="primary" size="40" width="4" class="mb-3"></v-progress-circular>
      <div class="caption grey--text">Loading enrollment data...</div>
    </div>

    <div v-else>
      <!-- Plan Section -->
      <div v-if="planName" class="mb-6">
        <div class="d-flex align-center mb-3">
          <v-avatar size="32" color="primary" class="mr-3">
            <v-icon color="white" size="20">mdi-file-document-outline</v-icon>
          </v-avatar>
          <h3 class="title font-weight-bold mb-0">Plan</h3>
        </div>
        <v-card outlined class="pa-4">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-check-circle</v-icon>
            <span class="subtitle-1 font-weight-medium">{{ planName }}</span>
          </div>
        </v-card>
      </div>

      <!-- Benefits Section -->
      <div class="mb-6">
        <div class="d-flex align-center mb-3">
          <v-avatar size="32" color="success" class="mr-3">
            <v-icon color="white" size="20">mdi-heart-pulse</v-icon>
          </v-avatar>
          <h3 class="title font-weight-bold mb-0">Benefits</h3>
        </div>
        <div v-if="benefitsList && benefitsList.length > 0" class="d-flex flex-wrap">
          <v-chip
            v-for="(benefit, index) in benefitsList.slice(0, 10)"
            :key="'b-' + index"
            class="mr-2 mb-2 benefit-chip"
            color="success"
            outlined
            small
          >
            <v-icon x-small left color="success">mdi-check-circle</v-icon>
            <span style="color: #4caf50; font-size: 11px;">{{ benefit }}</span>
          </v-chip>
          <div v-if="benefitsList.length > 10" class="caption grey--text mt-2" style="width: 100%;">
            + {{ benefitsList.length - 10 }} more benefits
          </div>
        </div>
        <div v-else>
          <v-card outlined class="pa-3">
            <div class="caption grey--text text--lighten-1">
              <v-icon small class="mr-1">mdi-information-outline</v-icon>
              No benefits found for this member.
            </div>
          </v-card>
        </div>
      </div>

      <!-- PCP Section (only if plan is PHC) -->
      <div v-if="hasPHCPlan === true" class="mb-6">
        <div class="d-flex align-center mb-3">
          <v-avatar size="32" color="success" class="mr-3">
            <v-icon color="white" size="20">mdi-medical-bag</v-icon>
          </v-avatar>
          <h3 class="title font-weight-bold mb-0">Primary Care Provider (PCP)</h3>
        </div>

        <v-alert
          v-if="pendingPcpChange && pendingPcpChange.effectiveDateDisplay"
          type="info"
          dense
          outlined
          class="mb-3"
        >
          <strong>Scheduled PCP change:</strong>
          {{ pendingPcpChange.providerName || 'New provider' }} effective
          {{ pendingPcpChange.effectiveDateDisplay }}.
          <span v-if="customerServiceNotice" class="d-block mt-2">{{ customerServiceNotice }}</span>
        </v-alert>

        <v-card
          class="mb-3 pcp-card"
          outlined
          :class="pcpData ? 'pcp-selected' : 'pcp-unselected'"
        >
          <v-card-text class="pa-4">
            <div class="d-flex align-start">
              <v-avatar size="48" :color="pcpData ? 'success' : 'grey lighten-2'" class="mr-4">
                <v-icon :color="pcpData ? 'white' : 'grey darken-1'" size="24">mdi-medical-bag</v-icon>
              </v-avatar>
              <div
                class="flex-grow-1"
                :class="{ 'clickable-provider': pcpData }"
                @click="pcpData ? showProviderDetails(pcpData, 'PCP') : null"
              >
                <div class="d-flex align-center mb-2">
                  <h4 class="subtitle-1 font-weight-bold mr-2 mb-0">Primary Care Provider</h4>
                  <v-chip v-if="pcpData" x-small color="success" text-color="white">
                    <v-icon x-small left>mdi-check</v-icon>
                    Selected
                  </v-chip>
                  <v-chip v-else x-small color="warning" text-color="white">
                    <v-icon x-small left>mdi-alert</v-icon>
                    Not Selected
                  </v-chip>
                </div>
                <div v-if="pcpData" class="pl-4">
                  <div class="body-1 font-weight-medium mb-1">
                    {{ pcpData.PROVIDER_NAME }}
                    <v-icon small class="ml-1 grey--text">mdi-information-outline</v-icon>
                  </div>
                  <div v-if="pcpData.PROVIDER_ID" class="caption grey--text mt-1">
                    NPI: {{ pcpData.PROVIDER_ID }}
                  </div>
                  <div
                    v-if="pcpData.PRACTICE_ID !== undefined && pcpData.PRACTICE_ID !== null && String(pcpData.PRACTICE_ID).trim() !== ''"
                    class="caption grey--text mt-1"
                  >
                    Practice ID: {{ pcpData.PRACTICE_ID }}
                  </div>
                  <div
                    v-if="pcpData.SERVICE_LOCATION_ADDRESS_1 || pcpData.SERVICE_LOCATION_ADDRESS_2"
                    class="caption grey--text mt-1"
                  >
                    <span v-if="pcpData.SERVICE_LOCATION_ADDRESS_1">{{ pcpData.SERVICE_LOCATION_ADDRESS_1 }}</span>
                    <span v-if="pcpData.SERVICE_LOCATION_ADDRESS_2">
                      <br v-if="pcpData.SERVICE_LOCATION_ADDRESS_1" />{{ pcpData.SERVICE_LOCATION_ADDRESS_2 }}
                    </span>
                  </div>
                  <div v-if="pcpData.SERVICE_LOCATION_CITY" class="caption grey--text d-flex align-center">
                    <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                    {{ pcpData.SERVICE_LOCATION_CITY }}, {{ pcpData.SERVICE_LOCATION_STATE }}
                  </div>
                  <div v-if="pcpData.BILLING_PHONE" class="caption grey--text d-flex align-center mt-1">
                    <v-icon x-small class="mr-1">mdi-phone</v-icon>
                    {{ pcpData.BILLING_PHONE }}
                  </div>
                  <div class="caption primary--text mt-1">
                    <v-icon x-small class="mr-1">mdi-cursor-pointer</v-icon>
                    Click to view full details
                  </div>
                </div>
                <div v-else class="pl-4">
                  <div class="body-2 grey--text">No PCP has been selected yet.</div>
                </div>
              </div>
              <div class="ml-auto">
                <v-btn
                  v-if="pcpData"
                  color="success"
                  outlined
                  small
                  :disabled="pcpModalOpen"
                  @click.stop="$emit('change-pcp', member)"
                >
                  <v-icon small left>mdi-pencil</v-icon>
                  Change
                </v-btn>
                <v-btn
                  v-else
                  color="success"
                  small
                  :disabled="pcpModalOpen"
                  @click.stop="handleSelectPCP"
                >
                  <v-icon small left>mdi-plus-circle</v-icon>
                  Select
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <v-alert
        v-if="pendingSpecialistChangesList && pendingSpecialistChangesList.length"
        type="info"
        dense
        outlined
        class="mb-4"
      >
        <div class="font-weight-medium mb-1">Scheduled specialist changes</div>
        <ul class="mb-0 pl-4">
          <li v-for="(row, idx) in pendingSpecialistChangesList" :key="'ps-' + idx">
            {{ row.specialty }}: {{ row.providerName || 'New provider' }} — effective {{ row.effectiveDateDisplay }}
          </li>
        </ul>
        <span v-if="customerServiceNotice" class="d-block mt-2">{{ customerServiceNotice }}</span>
      </v-alert>

      <!-- Specialists Section -->
      <div v-if="specialistsList && specialistsList.length > 0">
        <div class="d-flex align-center mb-3">
          <v-avatar size="32" color="primary" class="mr-3">
            <v-icon color="white" size="20">mdi-doctor</v-icon>
          </v-avatar>
          <h3 class="title font-weight-bold mb-0">Specialists</h3>
        </div>

        <div>
          <v-card
            v-for="(specialist, index) in specialistsList.slice(0, 8)"
            :key="'s-' + index"
            class="mb-3 specialist-card"
            outlined
            :class="specialist.provider ? 'specialist-selected' : 'specialist-unselected'"
          >
            <v-card-text class="pa-4">
              <div class="d-flex align-start">
                <v-avatar size="48" :color="specialist.provider ? 'primary' : 'grey lighten-2'" class="mr-4">
                  <v-icon :color="specialist.provider ? 'white' : 'grey darken-1'" size="24">mdi-stethoscope</v-icon>
                </v-avatar>
                <div
                  class="flex-grow-1"
                  :class="{ 'clickable-provider': specialist.provider }"
                  @click="specialist.provider ? showProviderDetails(specialist.provider, specialist.specialty) : null"
                >
                  <div class="d-flex align-center mb-2">
                    <h4 class="subtitle-1 font-weight-bold mr-2 mb-0">{{ specialist.specialty || 'Specialist' }}</h4>
                    <v-chip v-if="specialist.provider" x-small color="success" text-color="white">
                      <v-icon x-small left>mdi-check</v-icon>
                      Selected
                    </v-chip>
                    <v-chip v-else x-small color="warning" text-color="white">
                      <v-icon x-small left>mdi-alert</v-icon>
                      Not Selected
                    </v-chip>
                  </div>
                  <div v-if="specialist.provider" class="pl-4">
                    <div class="body-1 font-weight-medium mb-1">
                      {{ specialist.provider.PROVIDER_NAME }}
                      <v-icon small class="ml-1 grey--text">mdi-information-outline</v-icon>
                    </div>
                    <div v-if="specialist.provider.PROVIDER_ID" class="caption grey--text d-flex align-center mb-1">
                      <v-icon x-small class="mr-1">mdi-identifier</v-icon>
                      NPI: {{ specialist.provider.PROVIDER_ID }}
                    </div>
                    <div
                      v-if="specialist.provider.SERVICE_LOCATION_ADDRESS_1 || specialist.provider.SERVICE_LOCATION_ADDRESS_2"
                      class="caption grey--text mt-1"
                    >
                      <span v-if="specialist.provider.SERVICE_LOCATION_ADDRESS_1">{{ specialist.provider.SERVICE_LOCATION_ADDRESS_1 }}</span>
                      <span v-if="specialist.provider.SERVICE_LOCATION_ADDRESS_2">
                        <br v-if="specialist.provider.SERVICE_LOCATION_ADDRESS_1" />{{ specialist.provider.SERVICE_LOCATION_ADDRESS_2 }}
                      </span>
                    </div>
                    <div v-if="specialist.provider.SERVICE_LOCATION_CITY" class="caption grey--text d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                      {{ specialist.provider.SERVICE_LOCATION_CITY }}, {{ specialist.provider.SERVICE_LOCATION_STATE }}
                    </div>
                    <div v-if="specialist.provider.BILLING_PHONE" class="caption grey--text d-flex align-center mt-1">
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ specialist.provider.BILLING_PHONE }}
                    </div>
                    <div class="caption primary--text mt-1">
                      <v-icon x-small class="mr-1">mdi-cursor-pointer</v-icon>
                      Click to view full details
                    </div>
                  </div>
                  <div v-else class="pl-4">
                    <div class="body-2 grey--text">No specialist has been selected for this specialty yet.</div>
                  </div>
                </div>
                <div class="ml-auto">
                  <v-btn
                    v-if="specialist.provider"
                    color="primary"
                    outlined
                    small
                    :disabled="specialistModalOpen"
                    @click.stop="$emit('change-specialist', member, specialist.specialty)"
                  >
                    <v-icon small left>mdi-pencil</v-icon>
                    Change
                  </v-btn>
                  <v-btn
                    v-else
                    color="primary"
                    small
                    :disabled="specialistModalOpen"
                    @click.stop="$emit('select-specialist', member, specialist.specialty)"
                  >
                    <v-icon small left>mdi-plus-circle</v-icon>
                    Select
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>

    <!-- Provider Details Dialog -->
    <v-dialog v-model="showProviderDialog" max-width="600px" scrollable>
      <v-card v-if="selectedProvider">
        <v-card-title class="primary white--text">
          <v-icon class="mr-2" color="white">mdi-doctor</v-icon>
          <span class="font-weight-bold">Provider Details</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showProviderDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <div v-if="providerDialogMemberInfoRows.length" class="mb-4">
            <div class="text-subtitle-2 mb-2">Member information</div>
            <div v-for="(row, i) in providerDialogMemberInfoRows" :key="'pdm-' + i" class="mb-3">
              <div class="d-flex align-center mb-1">
                <v-icon class="mr-2 grey--text" small>mdi-account-outline</v-icon>
                <span class="caption grey--text text--darken-1 font-weight-medium">{{ row.label }}</span>
              </div>
              <div class="body-1 ml-8">{{ row.value }}</div>
            </div>
          </div>
          <v-divider v-if="providerDialogMemberInfoRows.length" class="my-4"></v-divider>

          <div class="mb-4">
            <div class="text-h5 font-weight-bold mb-1">{{ selectedProvider.PROVIDER_NAME }}</div>
            <v-chip color="primary" outlined class="mt-1">
              <v-icon small left>mdi-stethoscope</v-icon>
              {{ selectedProviderSpecialty }}
            </v-chip>
          </div>

          <v-divider class="my-4"></v-divider>

          <div v-if="selectedProvider.PROVIDER_ID" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-identifier</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Provider ID</span>
            </div>
            <div class="body-1 ml-8">{{ selectedProvider.PROVIDER_ID }}</div>
          </div>

          <div class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-human-male-female</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Gender</span>
            </div>
            <div class="body-1 ml-8">{{ selectedProviderGenderLine }}</div>
          </div>

          <div class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-certificate</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Board Certified</span>
            </div>
            <div class="body-1 ml-8">{{ selectedProviderBoardCertifiedLine }}</div>
          </div>

          <div v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_1 || selectedProvider.SERVICE_LOCATION_CITY" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-map-marker</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Address</span>
            </div>
            <div class="body-1 ml-8">
              <div v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_1">{{ selectedProvider.SERVICE_LOCATION_ADDRESS_1 }}</div>
              <div v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_2" class="mt-1">{{ selectedProvider.SERVICE_LOCATION_ADDRESS_2 }}</div>
              <div v-if="selectedProvider.SERVICE_LOCATION_CITY" class="mt-1">
                {{ selectedProvider.SERVICE_LOCATION_CITY }}{{ selectedProvider.SERVICE_LOCATION_STATE ? ', ' + selectedProvider.SERVICE_LOCATION_STATE : '' }}
                {{ selectedProvider.SERVICE_LOCATION_ZIP || '' }}
              </div>
            </div>
          </div>

          <div v-if="selectedProvider.BILLING_PHONE || selectedProvider.SERVICE_LOCATION_PHONE" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-phone</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Phone</span>
            </div>
            <div class="body-1 ml-8">
              <div v-if="selectedProvider.BILLING_PHONE" class="mb-1">
                <span class="caption grey--text mr-2">Billing:</span>
                <a :href="`tel:${selectedProvider.BILLING_PHONE}`" class="text-decoration-none">{{ selectedProvider.BILLING_PHONE }}</a>
              </div>
              <div v-if="selectedProvider.SERVICE_LOCATION_PHONE">
                <span class="caption grey--text mr-2">Service Location:</span>
                <a :href="`tel:${selectedProvider.SERVICE_LOCATION_PHONE}`" class="text-decoration-none">{{ selectedProvider.SERVICE_LOCATION_PHONE }}</a>
              </div>
            </div>
          </div>

          <div v-if="selectedProvider.LANGUAGE || selectedProvider.LANGUAGES" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-translate</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Languages</span>
            </div>
            <div class="ml-8">
              <div v-if="selectedProvider.LANGUAGES && Array.isArray(selectedProvider.LANGUAGES) && selectedProvider.LANGUAGES.length > 0">
                <v-chip v-for="(lang, index) in selectedProvider.LANGUAGES" :key="index" small class="mr-2 mb-1" color="primary" outlined>{{ lang }}</v-chip>
              </div>
              <div v-else-if="selectedProvider.LANGUAGE" class="body-1">
                <v-chip small color="primary" outlined>{{ selectedProvider.LANGUAGE }}</v-chip>
              </div>
            </div>
          </div>

          <div v-if="selectedProvider.PRIMARY_SPECIALTY" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-hospital-box</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Primary Specialty</span>
            </div>
            <div class="body-1 ml-8">{{ selectedProvider.PRIMARY_SPECIALTY }}</div>
          </div>

          <div v-if="selectedProvider.SERVICE_LOCATION_LAT && selectedProvider.SERVICE_LOCATION_LON" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-crosshairs-gps</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Location</span>
            </div>
            <div class="body-2 ml-8 grey--text">
              Coordinates: {{ selectedProvider.SERVICE_LOCATION_LAT }}, {{ selectedProvider.SERVICE_LOCATION_LON }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            v-if="selectedProviderSpecialty === 'PCP'"
            color="success"
            outlined
            :disabled="pcpModalOpen"
            @click="$emit('change-pcp', member)"
          >
            <v-icon left>mdi-pencil</v-icon>
            Change PCP
          </v-btn>
          <v-btn
            v-else
            color="primary"
            outlined
            :disabled="specialistModalOpen"
            @click="$emit('change-specialist', member, selectedProviderSpecialty)"
          >
            <v-icon left>mdi-pencil</v-icon>
            Change Specialist
          </v-btn>
          <v-btn color="primary" @click="showProviderDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { boardCertifiedDisplay, buildMemberInfoRows, providerGenderLine } from '../../utils/fhcProviderMemberDisplay';

export default {
  name: 'MemberEnrollmentDetails',
  props: {
    member: {
      type: Object,
      required: true,
    },
    insurance: {
      type: String,
      required: true,
    },
    pcpModalOpen: {
      type: Boolean,
      default: false,
    },
    specialistModalOpen: {
      type: Boolean,
      default: false,
    },
    enrollmentRefreshKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: true,
      showProviderDialog: false,
      selectedProvider: null,
      selectedProviderSpecialty: null,
      hasPHCPlan: false,
      benefitsList: [],
      specialistsList: [],
      pcpData: null,
      planName: null,
      pendingPcpChange: null,
      pendingSpecialistChangesList: [],
      customerServiceNotice: '',
    };
  },
  computed: {
    providerDialogMemberInfoRows() {
      return buildMemberInfoRows(this.member);
    },
    selectedProviderBoardCertifiedLine() {
      return boardCertifiedDisplay(this.selectedProvider) || '—';
    },
    selectedProviderGenderLine() {
      return providerGenderLine(this.selectedProvider);
    },
  },
  watch: {
    enrollmentRefreshKey(newVal, oldVal) {
      if (newVal === oldVal || newVal === 0) return;
      this.loadEnrollmentData();
    },
  },
  async mounted() {
    await this.loadEnrollmentData();
  },
  methods: {
    async loadEnrollmentData() {
      this.loading = true;
      try {
        const response = await axios.get(
          `/api/enrollment/member/${this.insurance}/${this.member.MEMBER_ID}`
        );
        if (response.data && response.data.enrollment) {
          const e = response.data.enrollment;

          const benefits = Array.isArray(e.benefits) ? e.benefits : (e.benefits ? [e.benefits] : []);

          let specialists = [];
          if (Array.isArray(e.specialists)) {
            specialists = e.specialists.map((s) =>
              s && typeof s === 'object'
                ? { specialty: s.specialty ? String(s.specialty) : null, provider: s.provider ? JSON.parse(JSON.stringify(s.provider)) : null }
                : s
            );
          } else if (e.specialists && typeof e.specialists === 'object') {
            specialists = Object.keys(e.specialists).map((key) => ({
              specialty: String(key),
              provider: e.specialists[key] ? JSON.parse(JSON.stringify(e.specialists[key])) : null,
            }));
          }

          let hasPHC = false;
          if (e.hasPHCPlan !== undefined) {
            hasPHC = Boolean(e.hasPHCPlan);
          } else if (e.planName) {
            hasPHC = String(e.planName).toUpperCase().indexOf('PHC') >= 0;
          }

          this.hasPHCPlan = hasPHC;
          this.benefitsList = JSON.parse(JSON.stringify(benefits));
          this.specialistsList = JSON.parse(JSON.stringify(specialists));
          this.pcpData = e.pcp ? JSON.parse(JSON.stringify(e.pcp)) : null;
          this.planName = e.planName ? String(e.planName).trim() || null : null;
          this.pendingPcpChange = e.pendingPcpChange ? { ...e.pendingPcpChange } : null;
          this.pendingSpecialistChangesList = Array.isArray(e.pendingSpecialistChangesList)
            ? e.pendingSpecialistChangesList.map((x) => ({ ...x }))
            : [];
          this.customerServiceNotice = e.customerServiceNotice ? String(e.customerServiceNotice) : '';
        }
      } catch {
        this.planName = null;
        this.hasPHCPlan = false;
        this.benefitsList = [];
        this.specialistsList = [];
        this.pcpData = null;
        this.pendingPcpChange = null;
        this.pendingSpecialistChangesList = [];
        this.customerServiceNotice = '';
      } finally {
        this.loading = false;
      }
    },
    handleSelectPCP() {
      this.$emit('select-pcp', this.member);
    },
    async showProviderDetails(provider, specialty) {
      this.selectedProvider = provider;
      this.selectedProviderSpecialty = specialty;
      this.showProviderDialog = true;

      if (provider && provider.PROVIDER_ID) {
        try {
          const params = {};
          if (provider.PRACTICE_ID !== undefined && provider.PRACTICE_ID !== null && String(provider.PRACTICE_ID).trim() !== '') {
            params.practiceId = provider.PRACTICE_ID;
          }
          const response = await axios.get(`/api/provider/${provider.PROVIDER_ID}`, { params });
          if (response.data && response.data.length > 0) {
            this.selectedProvider = { ...provider, ...response.data[0] };
          }
        } catch {
          // Keep existing provider data if fetch fails
        }
      }
    },
  },
};
</script>

<style scoped>
.specialist-card {
  transition: box-shadow 0.15s ease, border-left-color 0.15s ease;
  border-left: 3px solid transparent;
}

.specialist-card:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08) !important;
}

.specialist-selected {
  border-left-color: #4caf50 !important;
  background-color: #f1f8f4;
}

.specialist-unselected {
  border-left-color: #ff9800 !important;
  background-color: #fff8f0;
}

.clickable-provider {
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.clickable-provider:hover {
  opacity: 0.8;
}

.pcp-card {
  transition: box-shadow 0.15s ease, border-left-color 0.15s ease;
  border-left: 3px solid transparent;
}

.pcp-card:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08) !important;
}

.pcp-selected {
  border-left-color: #4caf50 !important;
  background-color: #f1f8f4;
}

.pcp-unselected {
  border-left-color: #ff9800 !important;
  background-color: #fff8f0;
}
</style>
